import { useRouter } from 'next/router';
import { ReactElement, ReactNode, useEffect } from 'react';

import { getFavoriteIds } from '@/api/customer';
import { getBoughtProductsList, getSimpleInfo } from '@/api/so';
import useGetDiscountCopy from '@/hooks/useGetDiscountCopy';
import usePageMountEffect from '@/hooks/usePageMountEffect';
import useSyncPromoBarCopy from '@/hooks/useSyncPromoBarCopy';
import useAccount from '@/store/account';
import useCart from '@/store/shopping';
import { captureAttributedEntryOnLanding } from '@/utils/attributed-entry/capture.client';
import { isSayWeeeApp } from '@/utils/common/device';
import { getLoginState } from '@/utils/common/user';

type Props = {
  children: ReactNode;
};

declare global {
  interface Window {
    bus: any;
  }
}

/**
 * 在页面路由切换时是否将tagInfo重置为null(避免在不需要展示68换购progress bar时展示出来)
 * @param pathName
 * @returns
 */
const shouldNotClearTagInfo = (pathName: string) => {
  const excludePaths = ['/category', '/promotion/recommend'];
  return excludePaths.some(path => pathName.startsWith(path));
};

export const useFetchAccount = () => {
  const { syncLoginState, syncUserBoughtData, syncFavoriteIds } = useAccount();
  const { handleSyncSimpleCart } = useCart();
  async function getSimpleCartData() {
    try {
      const res = await getSimpleInfo();
      if (res.result) {
        !!res?.object && handleSyncSimpleCart(res.object);
        !!res?.object && syncUserBoughtData(res.object?.purchased_products || []);
      }
    } catch (error) {}
  }
  const handlePageResume = () => {
    getSimpleCartData();
    const isLogin: boolean = getLoginState();
    if (!isLogin) {
      syncFavoriteIds([]);
    } else {
      getFavoriteIds().then(({ object }) => {
        syncFavoriteIds(object?.ids || []);
      });
    }
  };
  //同步登录状态和porder状态
  usePageMountEffect(() => {
    const isLogin: boolean = getLoginState();
    syncLoginState(isLogin);
    getSimpleCartData();
    const pageShow = event => {
      const historyTraversal =
        event.persisted || (typeof window.performance != 'undefined' && window.performance.navigation.type === 2);
      if (historyTraversal) {
        getSimpleCartData();
      }
    };

    if (isSayWeeeApp()) {
      document.addEventListener('onPageResume', handlePageResume);
    }

    window.addEventListener('pageshow', pageShow);

    return () => {
      window.removeEventListener('pageshow', pageShow);
      if (isSayWeeeApp()) {
        document.removeEventListener('onPageResume', handlePageResume);
      }
    };
  }, []);
};

export const useFetchFavorite = () => {
  const { syncFavoriteIds } = useAccount();
  //获取收藏列表
  usePageMountEffect(() => {
    const isLogin: boolean = getLoginState();
    if (!isLogin) {
      syncFavoriteIds([]);
    } else {
      getFavoriteIds().then(({ object }) => {
        syncFavoriteIds(object?.ids || []);
      });
    }
  }, []);
};

export const useReferer = () => {
  const { doSetReferrer, handleSyncTagInfo, handleSyncAddPopup } = useCart();
  const { userPreOrderLogged } = useAccount();
  const router = useRouter();
  useEffect(() => {
    if (!userPreOrderLogged) {
      return;
    }
    function handleRouteChangeStart() {
      setTimeout(() => {
        doSetReferrer(router?.asPath || '');
      });
      window.requestAnimationFrame(() => {
        handleSyncAddPopup(null);
      });
    }
    function routeChangeComplete() {
      const pathName = router?.pathname;
      /**
       * 68换购progress bar在分类页路由下切换时，需要保持bar展示
       * 68换购progress bar在跳转到promotion/recommend页面时需要展示出来
       * 其他路由重置bar数据
       */
      if (!shouldNotClearTagInfo(pathName)) {
        handleSyncTagInfo(null);
      }
    }
    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', routeChangeComplete);
    return function cleanup() {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', routeChangeComplete);
    };
  }, [JSON.stringify(router.asPath), userPreOrderLogged]);
};

/**
 * 广告外链归因（通道 B）：页面 hydrate 后以 document.referrer 写入或校正 cookie。
 * 挂载于 GlobalComponent，非 App 内自动执行，业务页面无需单独调用。
 */
export const useAttributedEntryCapture = () => {
  useEffect(() => {
    if (isSayWeeeApp()) {
      return;
    }
    captureAttributedEntryOnLanding();
  }, []);
};

export default function GlobalComponent({ children }: Props) {
  useFetchAccount();
  useReferer();
  useFetchFavorite();
  useGetDiscountCopy();
  useSyncPromoBarCopy();
  useAttributedEntryCapture();

  return children as ReactElement<any>;
}

export const GlobalEmptyComponent = ({ children }: Props) => {
  return children as ReactElement<any>;
};

export const generateGlobalComponent = (hooks: any[]) => ({ children }: Props) => {
  hooks.forEach(hook => {
    hook?.();
  });
  return children as ReactElement<any>;
};
